import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { ApiThreeDS } from '../openapi';
import { useApiClient } from '../contexts/ApiContext';
import { useHasRole } from './access';
import { useIsCardsAvailable } from './customers';
import { mapResponse } from './helpers';
import { useEffect, useState } from 'react';
import { addSeconds, differenceInMilliseconds, min } from 'date-fns';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';

export function useThreeDSListQuery() {
  const client = useApiClient();
  const isAdmin = useHasRole('ADMIN');
  const isCardsAvailable = useIsCardsAvailable();

  const query = useQuery({
    queryKey: ['3ds'],
    queryFn: ({ signal }) =>
      client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .GET('/threeDS' as any, {
          signal,
        })
        .then(mapResponse)
        .then((data: { threeDSs: ApiThreeDS[] }) => data.threeDSs),
    retry: true,
    refetchInterval: 10_000,
    enabled: isAdmin && isCardsAvailable,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      for (const it of query.data) {
        queryClient.setQueryData(['3ds', it.id], it);
      }
    }
  }, [query.data, queryClient]);

  return query;
}

export function useActiveThreeDSList() {
  const listQuery = useThreeDSListQuery();
  const [filtered, setFiltered] = useState<ApiThreeDS[]>([]);

  useEffect(() => {
    if (listQuery.data) {
      setFiltered(listQuery.data);
    }
  }, [listQuery.data]);

  useEffect(() => {
    if (!filtered.length) return;
    const minDate = min(filtered.map((it) => addSeconds(it.createdAt, it.lifeTimeInSec - 1)));
    const id = setTimeout(
      () => {
        setFiltered((prev) =>
          prev.filter((it) => addSeconds(it.createdAt, it.lifeTimeInSec - 1) > new Date()),
        );
      },
      differenceInMilliseconds(minDate, new Date()) - 100,
    );

    return () => clearTimeout(id);
  });

  return filtered;
}

export function useThreeDSQuery({ id }: { id: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['3ds', id],

    queryFn: ({ signal }) =>
      client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .GET(`/threeDS/${id}` as any, {
          signal,
        })
        .then(mapResponse)
        .then((data: { threeDS: ApiThreeDS }) => data.threeDS),
  });
}

export function useThreeDSResolveMutation({ id }: { id: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['3ds', id, 'resolve'],
    mutationFn: ({ action }: { action: 'approve' | 'decline' }) =>
      client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .POST(`/threeDS/${id}/${action}` as any, {
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse)
        .then((data: { threeDS: ApiThreeDS }) => data.threeDS),
    onSuccess: (threeDS) => {
      queryClient.setQueryData(['3ds', threeDS.id], threeDS);
    },
  });
}

export function useThreeDSCodeSuspenseQuery({ code }: { code: string }) {
  const client = useApiClient();
  const query = useSuspenseQuery({
    queryKey: ['3ds', code],

    queryFn: () =>
      client
        .GET(`/threeDS/shortCode/${code}` as unknown as never)
        .then(mapResponse)
        .then((data: { threeDS: ApiThreeDS }) => data.threeDS),
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      queryClient.setQueryData(['3ds', query.data.id], query.data);
    }
  }, [query.data, queryClient]);

  return query;
}

export function useUnauthenticated3dsResolveMutation({ code }: { code: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['3ds', code, 'resolve'],
    mutationFn: ({ action }: { action: 'approve' | 'decline' }) =>
      client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .POST(`/threeDS/shortCode/${code}/${action}` as any, {
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse)
        .then((data: { threeDS: ApiThreeDS }) => data.threeDS),
    onSuccess: (threeDS) => {
      queryClient.setQueryData(['3ds', code], threeDS);
      queryClient.setQueryData(['3ds', threeDS.id], threeDS);
    },
  });
}
